/**
 * QuotePage — one CIS quote, `/quotes/:quoteNo` (docs/features/
 * order-to-invoice/40_quotes_module_spec.md §10; the journeys branch's
 * round 19 is the visual spec, as far as phase Q1 supports it).
 *
 * One object card, as the order's: the header (status pill, the customer,
 * the value; facts: lines, contact, expires, needed by, what it replaces)
 * with "Details ▾" after them, which opens the send options under them; then
 * the action box (doc 27: waiting on the customer to decide; a quote email
 * that did not go is the one thing to do; a closed quote its closing line,
 * linking on to its order or the quote that replaced it); then the chain —
 * Published → Customer decision → Order (a draft starts with a Draft step) — each
 * step's ▾ opening its panel under the rail. Activity is in the ⋯, a side sheet,
 * with Withdraw while the quote is open (Discard, for a draft). All of it is in the query (`?step=published&line=.02`,
 * `details=1`, `activity=1`, `now=1`), so a deep link and Back land there.
 *
 * Every write posts to the URL the quote's view carries for it, and a step
 * the view offers no URL for is not drawn. Staff build a draft quote, and revise
 * a published, expired or declined one, in One only (Ben, 19 Oct, ruling 16 of
 * docs/features/object-ui-and-skills/19_Q_draft_quote_builder_spec.md): this page
 * shows the quote and links to its record in One. A draft can still be published
 * (prices fixed, nobody told) and shared (the contact is emailed their link, as
 * often as asked); a published quote that was never shared is not yet with the
 * customer.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { ChevronDown, MoreHorizontal } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { QuoteCommand, QuoteView } from "@shared-core/data/quote-types";
import { type QuoteArtworkCommand } from "@shared-core/data/artwork-staff-types";
import { findVersion } from "@shared-core/data/artwork-staff-actions";
import {
  orderStepFailure,
  type OrderStepFailure,
} from "@shared-core/domain/order-to-invoice-presentation";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import { isFeatureEnabled } from "@shared-core/config/features";
import type { RailStep } from "@shared-core/domain/lifecycle-rail";
import {
  formatDateOrDash,
  formatQuotedPrice,
} from "@shared-core/domain/formatters";
import {
  blockedCommandReason,
  describeDraftChecks,
  draftChecksOf,
} from "@shared-core/domain/quote-draft-checks";
import {
  contactName,
  isQuoteRailStep,
  quoteLineNo,
  quoteStepFailure,
  railFromQuote,
  type QuoteStepFailure,
} from "@shared-core/domain/quote-presentation";
import { paths, type QuotePageView } from "@shared-core/routing/routes";
import { Alert, AlertDescription } from "../app/components/primitives/alert";
import { Button } from "../app/components/primitives/button";
import { Card } from "../app/components/primitives/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../app/components/primitives/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../app/components/primitives/sheet";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../app/components/primitives/tooltip";
import { cn } from "../app/components/primitives/utils";
import { LifecycleRail } from "../app/components/patterns/LifecycleRail";
import { ObjectChip } from "../app/components/patterns/ObjectChip";
import {
  ObjectHeader,
  type Crumb,
} from "../app/components/patterns/ObjectHeader";
import { UploadFilesFormDialog } from "../app/components/domain/artwork/ArtworkDialogs";
import { NameVersionsDialog } from "../app/components/domain/orders/NameVersionsDialog";
import {
  ChangeOwnerDialog,
  ChooseContactDialog,
  ChooseDeliveryAddressDialog,
  ChooseQuantitiesDialog,
  ResolveAlternativeDialog,
} from "../app/components/domain/quotes/DraftCheckDialogs";
import {
  PublishQuoteDialog,
  ShareQuoteDialog,
  WithdrawQuoteDialog,
} from "../app/components/domain/quotes/QuoteDialogs";
import {
  QuoteActionBox,
  QuoteDecisionPanel,
  QuoteDetailsPanel,
  QuoteLinesPanel,
  QuoteOrderPanel,
  QuoteStatusPill,
  QuoteTimeline,
} from "../app/components/domain/quotes/QuotePanels";

type LoadState =
  | { kind: "loading" }
  | { kind: "failed"; message: string }
  | { kind: "not_found" }
  | { kind: "loaded"; quote: QuoteView };

type OpenDialog =
  | { kind: "name_versions"; lineId: string }
  | { kind: "choose_quantities"; lineId: string }
  | { kind: "resolve_alternative"; lineId: string }
  | { kind: "change_owner" }
  | { kind: "choose_contact" }
  | { kind: "choose_delivery_address" }
  | { kind: "withdraw" }
  | { kind: "publish" }
  | { kind: "share" }
  | { kind: "upload_file"; versionId: string; replacing: boolean };

/**
 * A step quotes refuses for now ("hide or block"): drawn, but disabled, with
 * quotes' reason on hover and focus. A channel's draft with no contact yet
 * can't be published or shared until one is chosen.
 */
function BlockedButton({
  label,
  reason,
  variant,
}: {
  label: string;
  reason: string;
  variant: "default" | "outline";
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {/* A disabled button takes no focus or hover: the wrapper carries the reason. */}
        <span
          role="button"
          aria-disabled="true"
          tabIndex={0}
          aria-label={`${label} (not yet: ${reason})`}
        >
          <Button
            size="sm"
            variant={variant}
            disabled
            aria-hidden
            tabIndex={-1}
          >
            {label}
          </Button>
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">{reason}</TooltipContent>
    </Tooltip>
  );
}

function crumbsOf(quote: QuoteView): Crumb[] {
  const code = quote.customer.tharstern_customer_code;
  // A CIS-only customer has no staff customer page: the trail starts at Quotes.
  if (!code)
    return [{ label: "Quotes", to: paths.quotes() }, { label: quote.quote_no }];
  return [
    { label: "Customers", to: paths.customers() },
    {
      label: quote.customer.name,
      to: paths.customerDetail({ customerId: code }),
    },
    { label: "Quotes", to: paths.customerQuotes({ customerId: code }) },
    { label: quote.quote_no },
  ];
}

export function QuotePage() {
  const { quoteNo: rawNo } = useParams<{ quoteNo: string }>();
  const quoteNo = rawNo ? decodeURIComponent(rawNo) : "";
  const adapter = useDataAdapter();
  const capabilities = usePlatformCapabilities();
  const [searchParams, setSearchParams] = useSearchParams();
  const openStep = searchParams.get("step");
  const focusLineNo = searchParams.get("line");
  const isDetailsOpen = searchParams.get("details") === "1";
  const isActivityOpen = searchParams.get("activity") === "1";
  const isBoxOpen = searchParams.get("now") === "1";
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState<OpenDialog | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    setNotice(null);
    adapter
      .getQuote(quoteNo)
      .then((quote) => {
        if (cancelled) return;
        setState(quote ? { kind: "loaded", quote } : { kind: "not_found" });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          kind: "failed",
          message:
            err instanceof Error ? err.message : "Could not load the quote.",
        });
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, quoteNo]);

  // Every step: post to the quote's URL for it, show the re-read quote, or
  // hand the refusal back to the dialog to show beside its fields.
  const apply = useCallback(
    async (
      quote: QuoteView,
      command: QuoteCommand,
      done: string,
    ): Promise<QuoteStepFailure | null> => {
      setBusy(true);
      setNotice(null);
      try {
        const next = await adapter.applyQuoteCommand(quote, command);
        setState({ kind: "loaded", quote: next });
        setNotice(done);
        return null;
      } catch (err) {
        return quoteStepFailure(err);
      } finally {
        setBusy(false);
      }
    },
    [adapter],
  );

  // A file uploaded for the customer: post to the URL the quote's artwork read
  // carries for the part, show the re-read quote, or hand the refusal back to
  // the dialog to show beside its input.
  const uploadFile = useCallback(
    async (
      quote: QuoteView,
      command: QuoteArtworkCommand,
    ): Promise<OrderStepFailure | null> => {
      setBusy(true);
      setNotice(null);
      try {
        const next = await adapter.applyQuoteArtworkCommand(quote, command);
        setState({ kind: "loaded", quote: next });
        setNotice("File uploaded for the customer: it is checked now.");
        return null;
      } catch (err) {
        return orderStepFailure(err);
      } finally {
        setBusy(false);
      }
    },
    [adapter],
  );

  // A file at the URL the read carried, fetched with the caller's auth and saved.
  const downloadFile = useCallback(
    async (url: string) => {
      setBusy(true);
      setNotice(null);
      try {
        const { blob, filename } = await adapter.downloadArtworkFamilyFile(url);
        capabilities.downloadBlob(filename, blob);
      } catch (err) {
        setNotice(quoteStepFailure(err).summary);
      } finally {
        setBusy(false);
      }
    },
    [adapter, capabilities],
  );

  if (state.kind === "loading") {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-muted-foreground">Loading quote…</div>
      </div>
    );
  }
  if (state.kind === "failed") {
    return (
      <Alert variant="destructive" role="alert">
        <AlertDescription>
          <p>
            Could not load quote {quoteNo}: {state.message}
          </p>
        </AlertDescription>
      </Alert>
    );
  }
  if (state.kind === "not_found") {
    return (
      <Card className="p-12 text-center">
        <h1 className="mb-2 text-lg">Quote not found</h1>
        <p className="text-muted-foreground">
          There is no quote {quoteNo} that you can see. A Tharstern estimate
          opens under{" "}
          <Link to={paths.estimates()} className="underline">
            Estimates
          </Link>
          .
        </p>
      </Card>
    );
  }

  const { quote } = state;
  const lines = quote.lines;
  const isOpen = quote.status === "published";
  const isDraft = quote.status === "draft";
  // A channel's draft: what its owner still has to decide, said again as they publish.
  const openChecks = describeDraftChecks(draftChecksOf(quote));
  const publishBlockedBecause = blockedCommandReason(
    quote,
    "quotes.publish_quote",
  );
  const shareBlockedBecause = blockedCommandReason(quote, "quotes.share_quote");
  const customerCode = quote.customer.tharstern_customer_code;
  // Opening a step or Activity is a history entry, so Back closes it;
  // Details and the box replace theirs. Other query is kept.
  const setView = (
    patch: Partial<Record<keyof QuotePageView, string | null>>,
    { replace = false }: { replace?: boolean } = {},
  ) =>
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        for (const [key, value] of Object.entries(patch)) {
          if (value) next.set(key, value);
          else next.delete(key);
        }
        return next;
      },
      { replace },
    );
  const renderPanel = (step: RailStep) => {
    switch (step.id) {
      case "draft":
      case "published":
        return (
          <QuoteLinesPanel
            quote={quote}
            focusLineNo={focusLineNo}
            busy={busy}
            onNameVersions={(lineId) =>
              setDialog({ kind: "name_versions", lineId })
            }
            onChooseQuantities={(lineId) =>
              setDialog({ kind: "choose_quantities", lineId })
            }
            onResolveAlternative={(lineId) =>
              setDialog({ kind: "resolve_alternative", lineId })
            }
            artworkHandlers={{
              busy,
              onUpload: (versionId, replacing) =>
                setDialog({ kind: "upload_file", versionId, replacing }),
              onDownload: (url) => void downloadFile(url),
            }}
          />
        );
      case "decision":
        return <QuoteDecisionPanel quote={quote} />;
      case "order":
        return <QuoteOrderPanel quote={quote} />;
      default:
        return null;
    }
  };
  const naming =
    dialog?.kind === "name_versions"
      ? lines.find((l) => l.id === dialog.lineId)
      : undefined;
  const choosing =
    dialog?.kind === "choose_quantities"
      ? lines.find((l) => l.id === dialog.lineId)
      : undefined;
  const resolving =
    dialog?.kind === "resolve_alternative"
      ? lines.find((l) => l.id === dialog.lineId)
      : undefined;
  // The version a file is being uploaded for, with its line (the file column's dialog).
  const quoteArtwork = quote.artwork;
  const uploadingLine =
    dialog?.kind === "upload_file" && quoteArtwork
      ? quoteArtwork.lines.find((l) =>
          l.versions.some((v) => v.version_id === dialog.versionId),
        )
      : undefined;
  const uploadingVersion =
    dialog?.kind === "upload_file" && quoteArtwork
      ? findVersion(quoteArtwork, dialog.versionId)
      : null;
  const uploading =
    uploadingLine && uploadingVersion
      ? {
          line: uploadingLine,
          lineName:
            lines.find((l) => l.line_ref === uploadingLine.line_ref)
              ?.name_full ?? uploadingLine.line_no,
          version: uploadingVersion,
        }
      : null;

  return (
    // Room at the foot so the floating Feedback button never covers a row's step.
    <div className="mx-auto max-w-7xl space-y-4 pb-24">
      <ObjectHeader
        crumbs={crumbsOf(quote)}
        title={`Quote ${quote.quote_no} · ${quote.title}`}
        pills={
          <QuoteStatusPill status={quote.status} label={quote.status_label} />
        }
        counterpart={
          customerCode ? (
            <Link
              to={paths.customerDetail({ customerId: customerCode })}
              className="hover:underline"
            >
              {quote.customer.name}
            </Link>
          ) : (
            quote.customer.name
          )
        }
        value={formatQuotedPrice(quote.total_pence)}
        valueLabel={
          quote.has_alternatives && isOpen
            ? "ex VAT, at the first quantities"
            : "quoted, ex VAT"
        }
        facts={[
          {
            label: lines.length === 1 ? "Line" : "Lines",
            value:
              lines.length === 1 ? lines[0]!.name_full : String(lines.length),
          },
          isDraft
            ? quote.made_from_label
              ? { label: "Made from", value: quote.made_from_label }
              : { label: "Made by", value: quote.created_by }
            : { label: "Published to", value: contactName(quote) },
          ...(isDraft
            ? [
                {
                  label: "Owner",
                  value: quote.owner_name ?? "Not yet assigned",
                },
              ]
            : []),
          ...(isDraft
            ? [
                {
                  label: "Goes to",
                  value: quote.contact?.name ?? "No contact yet",
                },
              ]
            : []),
          ...(isOpen
            ? [
                { label: "Expires", value: formatDateOrDash(quote.expires_at) },
                {
                  label: "Link shared",
                  value: quote.last_shared_at
                    ? formatDateOrDash(quote.last_shared_at)
                    : "Not yet",
                },
              ]
            : isDraft
              ? []
              : [
                  {
                    label: "Published",
                    value: formatDateOrDash(quote.published_at),
                  },
                ]),
          ...(quote.required_on
            ? [
                {
                  label: "Needed by",
                  value: formatDateOrDash(quote.required_on),
                },
              ]
            : []),
          ...(quote.replaces
            ? [
                {
                  label: "Replaces",
                  value: (
                    <ObjectChip
                      label={quote.replaces.quote_no}
                      to={paths.quoteDetail({
                        quoteNo: quote.replaces.quote_no,
                      })}
                    />
                  ),
                },
              ]
            : []),
        ]}
        disclosure={
          <button
            type="button"
            aria-expanded={isDetailsOpen}
            aria-controls="quote-details"
            onClick={() =>
              setView(
                { details: isDetailsOpen ? null : "1" },
                { replace: true },
              )
            }
            className="inline-flex items-center gap-0.5 rounded-sm text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
          >
            Details
            <ChevronDown
              aria-hidden
              className={cn(
                "size-4 transition-transform",
                isDetailsOpen && "rotate-180",
              )}
            />
          </button>
        }
        actions={
          <>
            {/* Staff build and revise quotes in One only (Ben, 19 Oct, ruling 16): Classic shows the quote. */}
            {isFeatureEnabled("one") && (isDraft || quote.revise_url) && (
              <Button asChild size="sm" variant="outline">
                <Link
                  to={paths.objectRecord({
                    objectId: "quotes.quote",
                    recordKey: quote.quote_no,
                  })}
                >
                  {isDraft ? "Build in One" : "Revise in One"}
                </Link>
              </Button>
            )}
            {quote.set_contact_url && !quote.contact && (
              <Button
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={() => setDialog({ kind: "choose_contact" })}
              >
                Choose contact…
              </Button>
            )}
            {quote.publish_url && (
              <Button
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={() => setDialog({ kind: "publish" })}
              >
                Publish
              </Button>
            )}
            {isDraft && publishBlockedBecause && (
              <BlockedButton
                label="Publish"
                reason={publishBlockedBecause}
                variant="outline"
              />
            )}
            {isDraft && quote.share_url && (
              <Button
                size="sm"
                disabled={busy}
                onClick={() => setDialog({ kind: "share" })}
              >
                Publish and share…
              </Button>
            )}
            {isDraft && shareBlockedBecause && (
              <BlockedButton
                label="Publish and share…"
                reason={shareBlockedBecause}
                variant="default"
              />
            )}
            {!isDraft && quote.share_url && (
              <Button
                size="sm"
                variant={quote.last_shared_at ? "outline" : "default"}
                disabled={busy}
                onClick={() => setDialog({ kind: "share" })}
              >
                {quote.last_shared_at ? "Share again…" : "Share…"}
              </Button>
            )}
            {/* Not modal: its Activity item opens the side sheet, which takes focus. */}
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline" aria-label="More actions">
                  <MoreHorizontal aria-hidden className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {quote.set_contact_url && quote.contact && (
                  <DropdownMenuItem
                    onSelect={() => setDialog({ kind: "choose_contact" })}
                  >
                    Change contact…
                  </DropdownMenuItem>
                )}
                {quote.set_delivery_address_url && (
                  <DropdownMenuItem
                    onSelect={() =>
                      setDialog({ kind: "choose_delivery_address" })
                    }
                  >
                    Choose delivery address…
                  </DropdownMenuItem>
                )}
                {quote.change_owner_url && (
                  <DropdownMenuItem
                    onSelect={() => setDialog({ kind: "change_owner" })}
                  >
                    Change owner…
                  </DropdownMenuItem>
                )}
                {quote.withdraw_url && (
                  <DropdownMenuItem
                    onSelect={() => setDialog({ kind: "withdraw" })}
                  >
                    {isDraft ? "Discard draft…" : "Withdraw…"}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onSelect={() => setView({ activity: "1" })}>
                  Activity…
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        }
      >
        {isDetailsOpen && (
          <div
            id="quote-details"
            data-section-id="quote.details"
            className="border-t border-border pt-3"
          >
            <QuoteDetailsPanel quote={quote} />
          </div>
        )}
        <div data-section-id="quote.action_box">
          <QuoteActionBox
            quote={quote}
            isExpanded={isBoxOpen}
            onExpandedChange={(isExpanded) =>
              setView({ now: isExpanded ? "1" : null }, { replace: true })
            }
            onShowDraftLines={() => setView({ step: "draft", line: null })}
          />
        </div>
        <div className="border-t border-border pt-3">
          <LifecycleRail
            rail={railFromQuote(quote)}
            ariaLabel={`Where quote ${quote.quote_no} is`}
            renderPanel={renderPanel}
            openStepId={isQuoteRailStep(openStep) ? openStep : null}
            onOpenStepChange={(stepId) => setView({ step: stepId, line: null })}
          />
        </div>
      </ObjectHeader>

      {notice && (
        <p role="status" className="text-sm text-muted-foreground">
          {notice}
        </p>
      )}

      <Sheet
        open={isActivityOpen}
        onOpenChange={(open) => {
          if (!open) setView({ activity: null }, { replace: true });
        }}
      >
        <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>Activity</SheetTitle>
            <SheetDescription>
              Everything that has happened on quote {quote.quote_no}, newest
              first.
            </SheetDescription>
          </SheetHeader>
          <div className="px-4 pb-6" data-section-id="quote.activity">
            <QuoteTimeline entries={quote.timeline} />
          </div>
        </SheetContent>
      </Sheet>

      {naming && (
        <NameVersionsDialog
          key={naming.id}
          title={`Name versions · ${quoteLineNo(quote, naming)} ${naming.name_full}`}
          description="Each version is its own artwork. Its label and name can change while the quote is published; the number of versions and their quantities stay as quoted."
          versions={naming.versions}
          busy={busy}
          onClose={() => setDialog(null)}
          onSave={(versions) =>
            apply(
              quote,
              {
                kind: "set_version_labels",
                lineRef: naming.line_ref,
                input: { versions },
              },
              `Named the versions of ${quoteLineNo(quote, naming)}.`,
            )
          }
        />
      )}
      {uploading && (
        <UploadFilesFormDialog
          key={`${uploading.version.version_id}-${dialog?.kind === "upload_file" && dialog.replacing}`}
          target={uploading}
          replacing={dialog?.kind === "upload_file" && dialog.replacing}
          busy={busy}
          onClose={() => setDialog(null)}
          onSubmit={(command) => uploadFile(quote, command)}
        />
      )}
      {dialog?.kind === "withdraw" && (
        <WithdrawQuoteDialog
          quote={quote}
          busy={busy}
          onClose={() => setDialog(null)}
          onConfirm={() =>
            apply(
              quote,
              { kind: "withdraw" },
              isDraft
                ? `Discarded draft ${quote.quote_no}.`
                : `Withdrew ${quote.quote_no}.`,
            )
          }
        />
      )}
      {choosing && (
        <ChooseQuantitiesDialog
          key={choosing.id}
          quote={quote}
          line={choosing}
          busy={busy}
          onClose={() => setDialog(null)}
          onSave={(quantities) =>
            apply(
              quote,
              {
                kind: "choose_quantities",
                lineRef: choosing.line_ref,
                input: { quantities },
              },
              `Saved the quantities ${quoteLineNo(quote, choosing)} offers.`,
            )
          }
        />
      )}
      {resolving && (
        <ResolveAlternativeDialog
          key={resolving.id}
          quote={quote}
          line={resolving}
          busy={busy}
          onClose={() => setDialog(null)}
          onSave={(resolution) =>
            apply(
              quote,
              {
                kind: "resolve_possible_alternative",
                lineRef: resolving.line_ref,
                input: { resolution },
              },
              resolution === "make_quantity_option"
                ? `Made ${quoteLineNo(quote, resolving)} a quantity option of the line it repeated.`
                : resolution === "remove"
                  ? `Removed ${quoteLineNo(quote, resolving)} from the draft.`
                  : `Kept ${quoteLineNo(quote, resolving)} as an extra line.`,
            )
          }
        />
      )}
      {dialog?.kind === "choose_contact" && (
        <ChooseContactDialog
          quote={quote}
          busy={busy}
          onClose={() => setDialog(null)}
          onSave={(contact_person_id) =>
            apply(
              quote,
              { kind: "set_contact", input: { contact_person_id } },
              `${quote.quote_no} goes to ${
                (quote.contact_choices ?? []).find(
                  (c) => c.person_id === contact_person_id,
                )?.name ?? "the customer's person"
              }.`,
            )
          }
        />
      )}
      {dialog?.kind === "choose_delivery_address" && (
        <ChooseDeliveryAddressDialog
          quote={quote}
          busy={busy}
          addAddressHref={
            quote.customer.tharstern_customer_code
              ? paths.customerDetail({
                  customerId: quote.customer.tharstern_customer_code,
                })
              : null
          }
          onClose={() => setDialog(null)}
          onSave={(address_id) =>
            apply(
              quote,
              { kind: "set_delivery_address", input: { address_id } },
              `${quote.quote_no}'s goods go to ${
                (quote.delivery_address_choices ?? []).find(
                  (a) => a.address_id === address_id,
                )?.name ?? "the address chosen"
              }.`,
            )
          }
        />
      )}
      {dialog?.kind === "change_owner" && (
        <ChangeOwnerDialog
          quote={quote}
          busy={busy}
          onClose={() => setDialog(null)}
          onSave={(owner_user_id) =>
            apply(
              quote,
              { kind: "change_owner", input: { owner_user_id } },
              `Changed the owner of ${quote.quote_no}.`,
            )
          }
        />
      )}
      {dialog?.kind === "publish" && (
        <PublishQuoteDialog
          quote={quote}
          openChecks={openChecks}
          busy={busy}
          onClose={() => setDialog(null)}
          onConfirm={(expires_at) =>
            apply(
              quote,
              { kind: "publish", input: { expires_at } },
              `Published ${quote.quote_no}: its prices are fixed. Share it when you're ready.`,
            )
          }
        />
      )}
      {dialog?.kind === "share" && (
        <ShareQuoteDialog
          quote={quote}
          openChecks={openChecks}
          busy={busy}
          onClose={() => setDialog(null)}
          onConfirm={(expires_at) =>
            apply(
              quote,
              { kind: "share", input: { expires_at } },
              `Emailed ${contactName(quote)} a link to ${quote.quote_no}.`,
            )
          }
        />
      )}
    </div>
  );
}
